import { useState } from "react";
import { CreditCard, Smartphone, Banknote, Mail, Copy, Check } from "lucide-react";

/**
 * "Other ways to give" — alternative payment options alongside Stripe.
 * Each tile shows the platform, the donor-facing handle/instructions,
 * a short fee note so generous donors can pick the option that
 * delivers the most to the cause.
 *
 * Edit the OPTIONS array below to swap in real handles,
 * then set `live: true` on each option once the handle is verified.
 */
const OPTIONS = [
  {
    id: "stripe",
    label: "Credit / Debit Card",
    Icon: CreditCard,
    accent: "#d62288",
    handle: "Use the Donate buttons above ↑",
    handleNote: "Cards · Apple Pay · Google Pay",
    fee: "Fee: 2.9% + $0.30 per donation",
    keeps: "Queen keeps ~96¢ of every $1",
    copyable: false,
    live: true,
  },
  {
    id: "zelle",
    label: "Zelle",
    Icon: Smartphone,
    accent: "#7d289b",
    handle: "queenzlikeme@gmail.com",
    handleNote: "Send from any U.S. bank's Zelle",
    fee: "Fee: $0 — completely free",
    keeps: "Queen keeps 100¢ of every $1",
    copyable: true,
    live: true,
    qr: "/qr/qr-zelle-purple.png",
  },
  {
    id: "cashapp",
    label: "Cash App",
    Icon: Smartphone,
    accent: "#00d54b",
    handle: "$queenzlikeme",
    handleNote: "Tap, paste, send",
    fee: "Fee: $0 from balance / bank · 3% if you pay with card",
    keeps: "Queen keeps 100¢ of every $1 (from balance)",
    copyable: true,
    live: true,
  },
  {
    id: "venmo",
    label: "Venmo",
    Icon: Smartphone,
    accent: "#3d95ce",
    handle: "@QueenzLikeMe",
    handleNote: "Tap pay → enter handle",
    fee: "Fee: $0 from bank / balance · 3% from credit card",
    keeps: "Queen keeps 100¢ of every $1 (from bank)",
    copyable: true,
    live: false,
  },
  {
    id: "check",
    label: "Check by Mail",
    Icon: Mail,
    accent: "#d4af37",
    handle: "Queenz Like Me\nP.O. Box [address]\nCity, ST ZIP",
    handleNote: "Make checks payable to: Queenz Like Me",
    fee: "Fee: $0",
    keeps: "Queen keeps 100¢ of every $1",
    copyable: false,
    live: false,
  },
  {
    id: "items",
    label: "In-Kind Gifts",
    Icon: Banknote,
    accent: "#f6e27a",
    handle: "Email queenzlikeme@gmail.com",
    handleNote: "Snacks · supplies · event sponsorships",
    fee: "100% used directly in programming",
    keeps: "Queen receives 100% of the value",
    copyable: false,
    live: false,
  },
];

function CopyBtn({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      /* silent */
    }
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#7d289b] hover:text-[#d62288] transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function OtherWaysToGive() {
  return (
    <section
      id="other-ways-to-give"
      data-testid="other-ways-to-give"
      className="mt-14 max-w-5xl mx-auto rounded-3xl bg-white/[0.06] backdrop-blur-xl border border-white/20 p-7 sm:p-10"
    >
      <div className="text-center mb-7">
        <p className="font-script text-2xl text-[#f6e27a]">Other Ways to Give</p>
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mt-1">
          Pick the option that works best for you.
        </h3>
        <p className="mt-3 text-sm text-white/80 max-w-2xl mx-auto">
          Every method gets the gift to the same place — these notes show how
          much of your dollar reaches the queenz after platform fees.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {OPTIONS.filter((o) => o.live).map((o) => (
          <article
            key={o.id}
            data-testid={`give-option-${o.id}`}
            className="rounded-2xl bg-white/95 p-5 border-l-4 shadow-xl flex flex-col"
            style={{ borderLeftColor: o.accent }}
          >
            <div className="flex items-center gap-3">
              <div
                className="h-10 w-10 rounded-xl flex items-center justify-center"
                style={{ background: `${o.accent}22`, border: `1.5px solid ${o.accent}55` }}
              >
                <o.Icon className="h-5 w-5" style={{ color: o.accent }} />
              </div>
              <div>
                <h4 className="font-display font-bold text-[#1a0f14] leading-none">
                  {o.label}
                </h4>
                <p className="text-[11px] text-[#7c6470] mt-1">{o.handleNote}</p>
              </div>
            </div>

            <div className="mt-3 rounded-xl bg-[#fff8e1] border border-[#f0e1c0] px-3 py-2.5">
              <pre className="font-mono text-[13px] text-[#1a0f14] whitespace-pre-wrap break-words m-0 leading-snug">
                {o.handle}
              </pre>
              {o.copyable && (
                <div className="mt-1.5 flex justify-end">
                  <CopyBtn value={o.handle} testid={`give-option-${o.id}-copy`} />
                </div>
              )}
            </div>

            {o.qr && (
              <div className="mt-3 flex items-center gap-3">
                <img
                  src={o.qr}
                  alt={`${o.label} QR code`}
                  data-testid={`give-option-${o.id}-qr`}
                  className="h-24 w-24 rounded-lg border border-[#f0e1c0] bg-white"
                />
                <p className="text-[11px] text-[#7c6470] leading-snug">
                  Or scan with your bank's Zelle to send instantly.
                </p>
              </div>
            )}

            <p className="mt-3 text-xs font-semibold text-[#4a1466]">{o.fee}</p>
            <p className="mt-0.5 text-xs italic text-[#7d289b]">{o.keeps}</p>
          </article>
        ))}
      </div>

      <p
        data-testid="give-options-coming-soon"
        className="mt-5 text-sm text-center text-[#f6e27a] font-semibold"
      >
        Venmo &amp; check-by-mail options are coming soon.
      </p>

      <p className="mt-7 text-xs text-center text-white/70 max-w-3xl mx-auto leading-relaxed">
        <strong className="text-[#f6e27a]">Note:</strong> If Queenz Like Me holds active
        501(c)(3) status, your gift may be <em>tax-deductible</em> — a year-end giving
        statement will be emailed automatically for credit-card and check donations.
        Peer-to-peer apps (Zelle, Cash App, Venmo) do not generate receipts; keep your
        own transaction confirmation if you intend to claim the deduction.
      </p>
    </section>
  );
}
